import React, { useState, useEffect, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

import { getColorScheme, getCustomPalette, COLOR_SCHEME_OPTIONS } from './constants/backgroundSettings';
import Login from './pages/Login/Login';
import CourseSelection from './pages/CourseSelection/CourseSelection';
import TeacherDashboard from './pages/Dashboard/Teacher/TeacherDashboard';
import QuestionDetail from './pages/Question/QuestionDetail/QuestionDetail';
import StudentDetail from './pages/Student/StudentDetail/StudentDetail';
import ForumPage from './pages/Forum/ForumPage';
import NotebookList from './pages/Notebook/NotebookList/NotebookList';
import MarketplacePage from './pages/Notebook/MarketplacePage/MarketplacePage';
import NotebookDetail from './pages/Notebook/NotebookDetail/NotebookDetail';
import NotebookChatPage from './pages/Notebook/NotebookChatPage/NotebookChatPage';
import NotebookReviewPage from './pages/Notebook/NotebookReviewPage/NotebookReviewPage';
import NotebookLearningStatePage from './pages/Notebook/NotebookLearningStatePage/NotebookLearningStatePage';
import NotebookExerciseDetail from './pages/Notebook/NotebookExerciseDetail/NotebookExerciseDetail';
import AddExercisesFlow from './pages/Notebook/AddExercisesFlow/AddExercisesFlow';
import UploadNotebookPage from './pages/Notebook/UploadNotebookPage/UploadNotebookPage';
import QuestionsPage from './pages/Student/QuestionsPage/QuestionsPage';
import BookmarksPage from './pages/Student/BookmarksPage/BookmarksPage';
import MemoryPage from './components/Dashboard/Student/MemoryPage';
import MemoryDetailPage from './pages/Student/MemoryDetailPage';
import SettingsPage from './pages/Student/SettingsPage';
import TeacherSettingsPage from './pages/Teacher/SettingsPage';
import HomePage from './pages/Home/HomePage';
import { AuthContext } from './contexts/AuthContext';

function createAppTheme(schemeId) {
  const p = schemeId === 'custom'
    ? getCustomPalette()
    : (COLOR_SCHEME_OPTIONS[schemeId] || COLOR_SCHEME_OPTIONS.default).palette;
  return createTheme({
    palette: {
      mode: 'dark',
      primary: p.primary,
      secondary: p.secondary,
      background: p.background,
      text: p.text,
    },
    typography: {
      fontFamily: [
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
      ].join(','),
      button: {
        textTransform: 'none',
        fontWeight: 500,
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: 6, fontWeight: 500 },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 500 },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: ({ theme }) => ({
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
          }),
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: ({ theme }) => ({ color: theme.palette.text.primary }),
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: ({ theme }) => ({ color: theme.palette.text.primary }),
        },
      },
      MuiDialogContentText: {
        styleOverrides: {
          root: ({ theme }) => ({ color: theme.palette.text.primary }),
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.text.secondary,
            '&.Mui-focused': { color: theme.palette.primary.main },
          }),
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.text.primary,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255, 255, 255, 0.23)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255, 255, 255, 0.5)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
            },
          }),
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: ({ theme }) => ({ color: theme.palette.text.primary }),
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.text.primary,
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' },
          }),
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: ({ theme }) => ({
            '& .MuiInputBase-input': { color: theme.palette.text.primary },
          }),
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.text.secondary,
            '&.Mui-focused': { color: theme.palette.primary.main },
          }),
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          label: ({ theme }) => ({ color: theme.palette.text.primary }),
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: ({ theme }) => ({
            '&.MuiDialogTitle-root': { color: theme.palette.text.primary },
          }),
        },
      },
    },
  });
}

function App() {
  const [user, setUser] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [colorSchemeId, setColorSchemeId] = useState(() => getColorScheme());

  const theme = useMemo(() => createAppTheme(colorSchemeId), [colorSchemeId]);

  useEffect(() => {
    const handler = (e) => setColorSchemeId(e.detail ?? getColorScheme());
    window.addEventListener('colorSchemeChanged', handler);
    return () => window.removeEventListener('colorSchemeChanged', handler);
  }, []);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    const courseData = localStorage.getItem('selectedCourse');
    
    if (token && userData) {
      setUser(JSON.parse(userData));
      if (courseData) {
        setSelectedCourse(JSON.parse(courseData));
      }
    }
    setLoading(false);
  }, []);

  const login = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const selectCourse = (course) => {
    localStorage.setItem('selectedCourse', JSON.stringify(course));
    setSelectedCourse(course);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('selectedCourse');
    setUser(null);
    setSelectedCourse(null);
  };

  const updateUser = (updatedUserData) => {
    localStorage.setItem('user', JSON.stringify(updatedUserData));
    setUser(updatedUserData);
  };

  if (loading) {
    return null;
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthContext.Provider value={{ user, selectedCourse, login, selectCourse, logout, updateUser }}>
        <Router future={{ v7_startTransition: true }}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={!user ? <Login /> : <Navigate to="/courses" />} />
            
            {/* Course Selection - Available to all authenticated users */}
            {user && (
              <Route path="/courses" element={<CourseSelection />} />
            )}
            
            {/* Student Routes - Require course selection */}
            {user && user.role === 'student' && selectedCourse && (
              <>
                <Route path="/dashboard" element={<Navigate to="/questions" replace />} />
                <Route path="/questions" element={<QuestionsPage />} />
                <Route path="/questions/category/:category" element={<QuestionsPage />} />
                <Route path="/questions/notebook/:notebookId" element={<QuestionsPage />} />
                <Route path="/bookmarks" element={<BookmarksPage />} />
                <Route path="/memory" element={<MemoryPage />} />
                <Route path="/memory/:conversationId" element={<MemoryDetailPage />} />
                <Route path="/question/:id" element={<QuestionDetail />} />
                <Route path="/forum" element={<ForumPage />} />
                <Route path="/notebooks/:courseCode?" element={<NotebookList />} />
                <Route path="/notebook/:notebookId/learning-state" element={<NotebookLearningStatePage />} />
                <Route path="/notebook/:notebookId/chat" element={<NotebookChatPage />} />
                <Route path="/notebook/:notebookId/review" element={<NotebookReviewPage />} />
                <Route path="/notebook/:notebookId" element={<NotebookDetail />} />
                <Route path="/notebook-exercise/:exampleId" element={<NotebookExerciseDetail />} />
                <Route path="/settings" element={<SettingsPage />} />
              </>
            )}

            {/* Teacher Routes - Require course selection */}
            {user && user.role === 'teacher' && selectedCourse && (
              <>
                <Route path="/dashboard" element={<TeacherDashboard />} />
                <Route path="/questions" element={<QuestionsPage />} />
                <Route path="/questions/category/:category" element={<QuestionsPage />} />
                <Route path="/questions/notebook/:notebookId" element={<QuestionsPage />} />
                <Route path="/teacher/settings" element={<TeacherSettingsPage />} />
                <Route path="/question/:id" element={<QuestionDetail />} />
                <Route path="/student/:studentId" element={<StudentDetail />} />
                <Route path="/forum" element={<ForumPage />} />
                <Route path="/marketplace" element={<MarketplacePage />} />
                <Route path="/notebooks/:courseCode?" element={<NotebookList />} />
                <Route path="/notebook/:notebookId/learning-state" element={<NotebookLearningStatePage />} />
                <Route path="/notebook/:notebookId/chat" element={<NotebookChatPage />} />
                <Route path="/notebook/:notebookId/review" element={<NotebookReviewPage />} />
                <Route path="/notebook/:notebookId/add-exercises" element={<AddExercisesFlow />} />
                <Route path="/notebook/:notebookId" element={<NotebookDetail />} />
                <Route path="/notebook-exercise/:exampleId" element={<NotebookExerciseDetail />} />
                <Route path="/upload-notebook" element={<UploadNotebookPage />} />
              </>
            )}

            {/* Default redirect */}
            <Route 
              path="*" 
              element={
                <Navigate to={
                  !user ? "/" : 
                  !selectedCourse ? "/courses" : 
                  user.role === 'student' ? "/questions" : "/dashboard"
                } />
              } 
            />
          </Routes>
        </Router>
      </AuthContext.Provider>
    </ThemeProvider>
  );
}

export default App;

